import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  PricingPlansSettings,
  PricingPlansSettingsCreatedEnvelope,
  PricingPlansSettingsUpdatedEnvelope,
  getPricingPlansSettings as universalGetPricingPlansSettings,
  updatePricingPlansSettings as universalUpdatePricingPlansSettings,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/pricing-plans' };

export function getPricingPlansSettings(
  httpClient: HttpClient
): GetPricingPlansSettingsSignature {
  return () =>
    universalGetPricingPlansSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface GetPricingPlansSettingsSignature {
  /**
   * Retrieves a site's Pricing Plans settings.
   *
   * A site whose settings were never created receives the default settings instead: `taxableAddress` is `BILLING`, `guestCheckoutSettings.enabled` is `false`, and `invoiceSettings.sendInvoiceOnPayment` is `false`.
   *
   * Default settings are returned without being stored, so a subsequent call to [Update Pricing Plans Settings](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans-settings/update-pricing-plans-settings) that passes the returned `revision` can fail.
   * @returns Retrieved Pricing Plans settings.
   */
  (): Promise<NonNullablePaths<PricingPlansSettings, `taxableAddress`, 0>>;
}

export function updatePricingPlansSettings(
  httpClient: HttpClient
): UpdatePricingPlansSettingsSignature {
  return (
    pricingPlansSettings: NonNullablePaths<PricingPlansSettings, `revision`, 0>
  ) =>
    universalUpdatePricingPlansSettings(
      pricingPlansSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePricingPlansSettingsSignature {
  /**
   * Updates a site's Pricing Plans settings.
   *
   * Each time the Pricing Plans settings are updated, `revision` increments by 1. The current `revision` must be passed when updating the Pricing Plans settings. This ensures you're working with the latest Pricing Plans settings and prevents unintended overwrites.
   *
   * Only the fields you include are updated. Fields you omit keep their current values, and read-only fields are ignored if you pass them.
   * @param - Pricing Plans settings to update.
   * @returns Updated Pricing Plans settings.
   */
  (
    pricingPlansSettings: NonNullablePaths<PricingPlansSettings, `revision`, 0>
  ): Promise<NonNullablePaths<PricingPlansSettings, `taxableAddress`, 0>>;
}

export const onPricingPlansSettingsUpdated = /*#__PURE__*/ EventDefinition(
  'wix.pricing_plans.settings.v1.pricing_plans_settings_updated',
  true,
  (event: PricingPlansSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<PricingPlansSettingsUpdatedEnvelope>();
export const onPricingPlansSettingsCreated = /*#__PURE__*/ EventDefinition(
  'wix.pricing_plans.settings.v1.pricing_plans_settings_created',
  true,
  (event: PricingPlansSettingsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PricingPlansSettingsCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Asset,
  AssignedFromFloatingReason,
  BaseEventMetadata,
  BillingReference,
  BooleanFeature,
  CancelRequestedReason,
  CancellationDetails,
  ContractSwitchClassification,
  ContractSwitchReason,
  ContractSwitchType,
  ContractSwitched,
  ContractSwitchedReason,
  Cycle,
  CycleCycleSelectorOneOf,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  Feature,
  FeatureCancelled,
  FeatureCancelledReasonOneOf,
  FeatureContext,
  FeatureDisabled,
  FeatureDisabledReasonOneOf,
  FeatureEnabled,
  FeatureEnabledReasonOneOf,
  FeatureEvent,
  FeatureEventEventOneOf,
  FeaturePeriod,
  FeatureQuantityInfoOneOf,
  FeatureQuotaUsageChanged,
  FeatureUpdated,
  FeatureUpdatedPreviousQuantityInfoOneOf,
  FeatureUpdatedReasonOneOf,
  GetPricingPlansSettingsRequest,
  GetPricingPlansSettingsResponse,
  GuestCheckoutSettings,
  IdentificationData,
  IdentificationDataIdOneOf,
  Initiator,
  Interval,
  IntervalUnit,
  InvoiceSettings,
  ManualFeatureCreationReason,
  ManualSyncReason,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  MigratedFromLegacyReason,
  Namespace,
  NamespaceChanged,
  NewFeatureReason,
  OdeditorAssigned,
  OdeditorUnassigned,
  OneTime,
  PicassoAssigned,
  PicassoUnassigned,
  PriceIncreaseTrigger,
  PricingPlansSettings,
  PricingPlansSettingsCreatedEnvelope,
  PricingPlansSettingsUpdatedEnvelope,
  ProductAdjustment,
  ProductPriceIncreaseData,
  ProviderName,
  QuotaExhaustedReason,
  QuotaFeature,
  QuotaInfo,
  QuotaInfoEntry,
  ReactivationData,
  ReactivationReasonEnum,
  ReassignedFromSiteReason,
  ReassignedToAnotherSiteReason,
  RecurringChargeAttemptFailed,
  RecurringChargeFailureReason,
  RecurringChargeSucceeded,
  RefundType,
  ReplacedByAnotherSubscriptionReason,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Subscription,
  SubscriptionAssigned,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionCancelled,
  SubscriptionCreated,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionMigratedFromVista,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  SubscriptionStatus,
  SubscriptionTransferred,
  SubscriptionUnassigned,
  SubscriptionUpdated,
  SubscriptionVistaMigrationReverted,
  TaxableAddress,
  TransferredFromAnotherAccountReason,
  TransferredToAnotherAccountReason,
  UnAssingedToFloatingReason,
  UnassignReason,
  UpdatePricingPlansSettingsRequest,
  UpdatePricingPlansSettingsResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.universal.js';
